import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, Lock, Trash2, GitMerge, Edit2, Loader2, Split, Camera } from 'lucide-react'

import { dramaApi, dramaQueryKeys, pickLocalized } from '@/lib/api/drama'
import type { DramaCharacter, DramaLang } from '@/types/drama'
import { AuthedImage } from '@/components/drama/AuthedImage'
import { dramaT } from '@/components/drama/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'

interface Props {
  projectId: string
  characters: DramaCharacter[]
  lang: DramaLang
}

const ROLE_VARIANT: Record<string, 'default' | 'info' | 'secondary' | 'warning' | 'destructive'> = {
  female_lead: 'info',
  male_lead: 'info',
  antagonist: 'destructive',
  supporting: 'secondary',
  extra: 'secondary',
}

// Auto-clustered identities that the VLM couldn't confidently name are saved as
// "Unnamed person N" / "未命名角色 N". They're noise for the editor — hide them
// from the gallery (the underlying data is kept; they can still be merged via
// the rename flow if ever needed). Exported so the tab badge count stays in
// sync with what this panel actually renders.
export function isUnnamed(c: DramaCharacter): boolean {
  const names = [c.display_name, c.display_name_i18n?.en, c.display_name_i18n?.zh]
  return names.some(
    (n) => !!n && (/^unnamed person/i.test(n.trim()) || /^未命名角色/.test(n.trim())),
  )
}

export function CharacterTable({ projectId, characters, lang }: Props) {
  const t = dramaT(lang)
  const queryClient = useQueryClient()
  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeSource, setMergeSource] = useState<string>('')
  const [mergeTarget, setMergeTarget] = useState<string>('')

  const [renameOpen, setRenameOpen] = useState(false)
  const [renameCode, setRenameCode] = useState<string>('')
  const [renameValue, setRenameValue] = useState<string>('')
  const [renameAliases, setRenameAliases] = useState<string>('')

  const [splitOpen, setSplitOpen] = useState(false)
  const [splitSource, setSplitSource] = useState<string>('')
  const [splitNameA, setSplitNameA] = useState<string>('')
  const [splitNameB, setSplitNameB] = useState<string>('')

  // Custom avatar upload: a hidden file input is reused per card; `uploadCode`
  // tracks which card triggered it. `avatarVersion` busts the image cache after
  // a successful upload (the URL is otherwise identical).
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadCode, setUploadCode] = useState<string | null>(null)
  const [avatarVersion, setAvatarVersion] = useState<Record<string, number>>({})

  // Hide auto-clustered "Unnamed person N" identities from the gallery.
  const visibleCharacters = characters.filter((c) => !isUnnamed(c))

  const refresh = () => queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })

  const avatarUploadMutation = useMutation({
    mutationFn: (vars: { code: string; file: File }) =>
      dramaApi.uploadCharacterAvatar(projectId, vars.code, vars.file),
    onSuccess: (_data, vars) => {
      toast({ title: t('toast.avatarUpdated') })
      setAvatarVersion((prev) => ({ ...prev, [vars.code]: Date.now() }))
      refresh()
    },
    onError: (e: any) =>
      toast({
        title: t('toast.avatarUploadFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      }),
  })

  const onPickAvatar = (code: string) => {
    setUploadCode(code)
    fileInputRef.current?.click()
  }

  const onAvatarFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file
    if (file && uploadCode) {
      avatarUploadMutation.mutate({ code: uploadCode, file })
    }
  }

  const mergeMutation = useMutation({
    mutationFn: () =>
      dramaApi.mergeCharacters(projectId, { source_code: mergeSource, target_code: mergeTarget }),
    onSuccess: () => {
      toast({ title: t('toast.merged'), description: `${mergeSource} → ${mergeTarget}` })
      setMergeOpen(false)
      setMergeSource('')
      setMergeTarget('')
      refresh()
    },
    onError: (e: any) =>
      toast({
        title: t('toast.mergeFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      }),
  })

  const renameMutation = useMutation({
    mutationFn: () =>
      dramaApi.patchCharacter(projectId, renameCode, {
        display_name: renameValue,
        aliases: renameAliases.split(',').map((s) => s.trim()).filter(Boolean),
      }),
    onSuccess: () => {
      toast({ title: t('toast.charUpdated'), description: renameValue })
      setRenameOpen(false)
      refresh()
    },
    onError: (e: any) =>
      toast({
        title: t('toast.updateFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      }),
  })

  const lockMutation = useMutation({
    mutationFn: (vars: { code: string; lock: boolean }) =>
      dramaApi.patchCharacter(projectId, vars.code, { is_locked: vars.lock }),
    onSuccess: () => refresh(),
  })

  const softDeleteMutation = useMutation({
    mutationFn: (code: string) => dramaApi.patchCharacter(projectId, code, { is_deleted: true }),
    onSuccess: () => {
      toast({ title: t('toast.charRemoved') })
      refresh()
    },
  })

  const splitMutation = useMutation({
    mutationFn: async () => {
      const newCodes = [`${splitSource}a`, `${splitSource}b`]
      const newNames = [splitNameA, splitNameB]
      return dramaApi.splitCharacter
        ? dramaApi.splitCharacter(projectId, {
            source_code: splitSource,
            new_codes: newCodes,
            new_display_names: newNames,
            appearance_assignments: {}, // user can re-assign later
          })
        : Promise.reject(new Error('split API not available'))
    },
    onSuccess: () => {
      toast({ title: t('toast.charSplit'), description: t('toast.charSplitDesc', splitSource) })
      setSplitOpen(false)
      setSplitNameA('')
      setSplitNameB('')
      refresh()
    },
    onError: (e: any) =>
      toast({
        title: t('toast.splitFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      }),
  })

  if (visibleCharacters.length === 0) {
    return (
      <div className="text-center py-16 border-2 border-dashed rounded-lg">
        <Users className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">
          {t('char.empty')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-spectrum-magenta" />
          <h3 className="text-sm font-semibold">{t('char.detected', visibleCharacters.length)}</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMergeOpen(true)}
            disabled={visibleCharacters.length < 2}
          >
            <GitMerge className="w-3.5 h-3.5 mr-1" /> {t('char.merge')}
          </Button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onAvatarFileChosen}
      />

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
        {visibleCharacters.map((c) => {
          const localName = pickLocalized(c.display_name_i18n ?? undefined, lang) || c.display_name
          const localDesc = pickLocalized(c.description_i18n ?? undefined, lang) || c.description || ''
          return (
          <div
            key={c.code}
            className="rounded-lg border bg-card overflow-hidden flex flex-col"
          >
            {/* Avatar area — portrait aspect so vertical drama frames aren't
                crop-heavy; object-cover keeps cards uniform; role badge floats
                over the top-right. */}
            <div className="relative h-56 bg-muted overflow-hidden">
              <CharacterAvatarImage
                projectId={projectId}
                code={c.code}
                name={localName}
                version={avatarVersion[c.code]}
              />
              {/* Manual avatar replace */}
              <button
                type="button"
                onClick={() => onPickAvatar(c.code)}
                disabled={avatarUploadMutation.isPending && uploadCode === c.code}
                className="absolute bottom-2 right-2 h-7 w-7 flex items-center justify-center rounded-full bg-background/85 backdrop-blur-sm shadow hover:bg-background transition-colors disabled:opacity-60"
                title={t('char.changeAvatar')}
              >
                {avatarUploadMutation.isPending && uploadCode === c.code ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Camera className="w-3.5 h-3.5" />
                )}
              </button>
              {c.role_type && (
                <Badge
                  variant={ROLE_VARIANT[c.role_type] ?? 'secondary'}
                  className="absolute top-2 right-2 bg-background/85 backdrop-blur-sm shadow"
                >
                  {c.role_type}
                </Badge>
              )}
              <code className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded bg-background/85 backdrop-blur-sm font-mono">
                {c.code}
              </code>
            </div>

            {/* Name + aliases + description (name & description follow the
                language toggle; aliases stay cross-lingual). */}
            <div className="px-3 pt-2.5 pb-1 flex-1 min-w-0">
              <div className="font-semibold text-sm truncate" title={localName}>
                {localName}
              </div>
              <div
                className="text-xs text-muted-foreground truncate min-h-[1rem]"
                title={c.aliases?.join(', ')}
              >
                {c.aliases?.length ? c.aliases.join(' · ') : ' '}
              </div>
              {localDesc && (
                <p
                  className="mt-1 text-xs text-muted-foreground line-clamp-6 whitespace-pre-line"
                  title={localDesc}
                >
                  {localDesc}
                </p>
              )}
            </div>

            {/* Action row */}
            <div className="flex items-center justify-around border-t px-1 py-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => {
                  setRenameCode(c.code)
                  setRenameValue(c.display_name)
                  setRenameAliases((c.aliases || []).join(', '))
                  setRenameOpen(true)
                }}
                title={t('char.editNameAliases')}
              >
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => {
                  setMergeSource(c.code)
                  setMergeOpen(true)
                }}
                title={t('char.mergeInto')}
              >
                <GitMerge className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => {
                  setSplitSource(c.code)
                  setSplitNameA(`${c.display_name} (A)`)
                  setSplitNameB(`${c.display_name} (B)`)
                  setSplitOpen(true)
                }}
                title={t('char.splitInto')}
              >
                <Split className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => lockMutation.mutate({ code: c.code, lock: true })}
                title={t('char.lock')}
              >
                <Lock className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-destructive"
                onClick={() => {
                  if (confirm(t('char.notRealConfirm', c.display_name))) {
                    softDeleteMutation.mutate(c.code)
                  }
                }}
                title={t('char.notReal')}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          )
        })}
      </div>

      {/* Merge dialog */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('char.mergeTitle')}</DialogTitle>
            <DialogDescription>
              {t('char.mergeDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('char.source')}</Label>
              <select
                value={mergeSource}
                onChange={(e) => setMergeSource(e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-background text-sm"
              >
                <option value="">{t('char.choose')}</option>
                {visibleCharacters.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.display_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>{t('char.target')}</Label>
              <select
                value={mergeTarget}
                onChange={(e) => setMergeTarget(e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-background text-sm"
              >
                <option value="">{t('char.choose')}</option>
                {visibleCharacters
                  .filter((c) => c.code !== mergeSource)
                  .map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.display_name}
                    </option>
                  ))}
              </select>
            </div>

            {/* Visual confirmation: show both avatars side-by-side. */}
            {(mergeSource || mergeTarget) && (
              <div className="flex items-center justify-center gap-4 py-2 rounded-lg bg-muted/30">
                <MergePreviewCard
                  projectId={projectId}
                  character={characters.find((c) => c.code === mergeSource)}
                  label={t('char.sourceShort')}
                  notSelectedLabel={t('char.notSelected')}
                  willBeRemoved
                />
                <GitMerge className="w-6 h-6 text-spectrum-magenta" />
                <MergePreviewCard
                  projectId={projectId}
                  character={characters.find((c) => c.code === mergeTarget)}
                  label={t('char.targetShort')}
                  notSelectedLabel={t('char.notSelected')}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)}>
              {t('char.cancel')}
            </Button>
            <Button
              onClick={() => mergeMutation.mutate()}
              disabled={!mergeSource || !mergeTarget || mergeMutation.isPending}
            >
              {mergeMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <GitMerge className="w-3.5 h-3.5 mr-1" />
              )}
              {t('char.merge')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split dialog */}
      <Dialog open={splitOpen} onOpenChange={setSplitOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('char.splitTitle', splitSource)}</DialogTitle>
            <DialogDescription>
              {t('char.splitDesc', splitSource)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{splitSource}a · {t('char.displayName')}</Label>
              <Input value={splitNameA} onChange={(e) => setSplitNameA(e.target.value)} />
            </div>
            <div>
              <Label>{splitSource}b · {t('char.displayName')}</Label>
              <Input value={splitNameB} onChange={(e) => setSplitNameB(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitOpen(false)}>
              {t('char.cancel')}
            </Button>
            <Button
              onClick={() => splitMutation.mutate()}
              disabled={!splitNameA || !splitNameB || splitMutation.isPending}
            >
              {splitMutation.isPending && (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              )}
              <Split className="w-3.5 h-3.5 mr-1" /> {t('char.split')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('char.editTitle')}</DialogTitle>
            <DialogDescription>
              {t('char.editDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Avatar preview + replace */}
            <div className="flex items-center gap-3">
              {renameCode && (
                <CharacterAvatar
                  projectId={projectId}
                  code={renameCode}
                  name={renameValue || renameCode}
                  size={56}
                  version={avatarVersion[renameCode]}
                />
              )}
              <div>
                <Label className="text-xs">{t('char.avatar')}</Label>
                <div className="mt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => renameCode && onPickAvatar(renameCode)}
                    disabled={avatarUploadMutation.isPending && uploadCode === renameCode}
                  >
                    {avatarUploadMutation.isPending && uploadCode === renameCode ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    ) : (
                      <Camera className="w-3.5 h-3.5 mr-1" />
                    )}
                    {t('char.changeAvatarBtn')}
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {t('char.avatarHint')}
                </p>
              </div>
            </div>
            <div>
              <Label>{t('char.displayName')}</Label>
              <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
            </div>
            <div>
              <Label>{t('char.aliases')}</Label>
              <Input
                value={renameAliases}
                onChange={(e) => setRenameAliases(e.target.value)}
                placeholder={t('char.aliasesPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>
              {t('char.cancel')}
            </Button>
            <Button onClick={() => renameMutation.mutate()} disabled={renameMutation.isPending}>
              {renameMutation.isPending && (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              )}
              {t('char.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/**
 * Round avatar tile for a character. Loads from the backend `/avatar` endpoint
 * which picks the best keyframe by VLM metadata (close-up, lighting, role
 * match, …). If the image 404s or the character has no appearances yet, falls
 * back to a colored initials tile.
 */
/**
 * Side-by-side preview card used in the merge dialog so the user can
 * visually confirm both characters before committing the merge.
 */
function MergePreviewCard({
  projectId,
  character,
  label,
  notSelectedLabel,
  willBeRemoved = false,
}: {
  projectId: string
  character: DramaCharacter | undefined
  label: string
  notSelectedLabel: string
  willBeRemoved?: boolean
}) {
  if (!character) {
    return (
      <div className="flex flex-col items-center gap-1.5 w-32 text-center">
        <div className="w-16 h-16 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center text-muted-foreground/40">
          ?
        </div>
        <div className="text-xs text-muted-foreground">{label}: {notSelectedLabel}</div>
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center gap-1.5 w-32 text-center">
      <CharacterAvatar
        projectId={projectId}
        code={character.code}
        name={character.display_name}
        size={64}
      />
      <div className="min-w-0 w-full">
        <div
          className={`text-sm font-medium truncate ${willBeRemoved ? 'line-through text-muted-foreground' : ''}`}
        >
          {character.display_name}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {label} · <code>{character.code}</code>
        </div>
      </div>
    </div>
  )
}

/**
 * Avatar that FILLS its parent container (used inside `aspect-[4/5]` slots
 * on the character card grid). Same fallback rules as `CharacterAvatar`:
 * if the image 404s the parent shows a colored initials block instead.
 */
function CharacterAvatarImage({
  projectId,
  code,
  name,
  version,
}: {
  projectId: string
  code: string
  name: string
  version?: number
}) {
  const [failed, setFailed] = useState(false)
  const initials = name.trim().slice(0, 1).toUpperCase() || '?'
  const hue =
    Array.from(code).reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) & 0xffffffff, 7) % 360
  // Retry the fresh image whenever a new upload bumps the version (clears the
  // initials fallback so the new avatar is fetched).
  useEffect(() => {
    setFailed(false)
  }, [version])
  if (failed) {
    return (
      <div
        className="w-full h-full flex items-center justify-center text-white font-bold"
        style={{
          fontSize: '4rem',
          background: `linear-gradient(135deg, hsl(${hue}, 60%, 55%), hsl(${(hue + 30) % 360}, 60%, 45%))`,
        }}
        aria-label={`${name} avatar`}
      >
        {initials}
      </div>
    )
  }
  return (
    <AuthedImage
      path={dramaApi.characterAvatarUrl(projectId, code, version)}
      alt={`${name} avatar`}
      onError={() => setFailed(true)}
      className="w-full h-full object-cover"
    />
  )
}

function CharacterAvatar({
  projectId,
  code,
  name,
  size = 40,
  version,
}: {
  projectId: string
  code: string
  name: string
  size?: number
  version?: number
}) {
  const [failed, setFailed] = useState(false)
  const initials = name.trim().slice(0, 1).toUpperCase() || '?'
  // Stable color from the code so the same character always gets the same hue.
  const hue =
    Array.from(code).reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) & 0xffffffff, 7) % 360
  useEffect(() => {
    setFailed(false)
  }, [version])
  if (failed) {
    return (
      <div
        className="rounded-full flex items-center justify-center text-white font-semibold shrink-0"
        style={{
          width: size,
          height: size,
          fontSize: Math.round(size * 0.42),
          background: `hsl(${hue}, 55%, 55%)`,
        }}
        aria-label={`${name} avatar`}
      >
        {initials}
      </div>
    )
  }
  return (
    <AuthedImage
      path={dramaApi.characterAvatarUrl(projectId, code, version)}
      alt={`${name} avatar`}
      onError={() => setFailed(true)}
      className="rounded-full object-cover shrink-0 bg-muted"
      style={{ width: size, height: size }}
    />
  )
}
